import { DropdownWithStatistic } from "components";
import { useTranslation } from "react-i18next";
import { Loader, Stat } from "ui";

import { RevenueBreakdownResponse, RevenueByVariant } from "../../../apis";
import { CategoryBreakdown, SectionCard } from "../atoms";
import { formatPercent, formatXof, shareOf } from "../financialFormat";

import styles from "./CapitalSection.module.scss";

interface CapitalSectionProps {
  data: RevenueBreakdownResponse | undefined;
}

const renderProductExtra = (variant: RevenueByVariant) => {
  const unitPrice = variant.units ? variant.costOfGoods / variant.units : 0;

  return (
    <div className={styles.unitPrice}>
      {variant.units} × {formatXof(unitPrice)}
    </div>
  );
};

const CapitalSection = ({ data }: CapitalSectionProps) => {
  const { t } = useTranslation();

  if (!data) {
    return (
      <SectionCard title="Inventory capital">
        <Loader />
      </SectionCard>
    );
  }

  const share = shareOf(data.totals.costOfGoods, data.totals.netRevenue);

  return (
    <SectionCard title="Inventory capital">
      <div className={styles.topBlock}>
        <Stat
          size="lg"
          title="Inventory capital · purchase cost of goods sold"
          amount={formatXof(data.totals.costOfGoods)}
        />
        <div className={styles.shareNote}>
          {formatPercent(share, 1)} {t("of period revenue")}
        </div>
      </div>

      <DropdownWithStatistic label="Capital by category" defaultOpen>
        <CategoryBreakdown
          level1={data.byLevel1}
          level2={data.byLevel2}
          variants={data.byVariant}
          metric="costOfGoods"
          totalValue={data.totals.costOfGoods}
          showComparison={false}
          shareLabel="of period capital"
          productsLabel="Capital by product"
          renderProductExtra={renderProductExtra}
        />
      </DropdownWithStatistic>
    </SectionCard>
  );
};

export default CapitalSection;
